import { useTranslation } from 'react-i18next';
import { useActiveContext } from '@/auth/ActiveContextProvider';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';

/**
 * Active-context selector (FR-4.12, FR-4.13, FR-6.4): a shadcn `Select` over the caller's eligible
 * (Tenant, Organization) pairs, sourced ONLY from `GET /v1/active-context/eligible-organizations`
 * (SL-0007 closure). Switching the selection calls `selectContext`, which re-evaluates the shell
 * with no re-authentication — the server still re-derives and enforces every permission per request
 * (FR-4.6/FR-6.3).
 */
export function ContextSelector() {
  const { status, eligible, active, selectContext } = useActiveContext();
  const { t } = useTranslation(['shell', 'common']);

  if (status === 'loading') {
    return (
      <Select disabled>
        <SelectTrigger size="sm" aria-label={t('shell:context.active')}>
          <SelectValue placeholder={t('common:loading')} />
        </SelectTrigger>
      </Select>
    );
  }

  if (status === 'error') {
    return <span className="text-destructive text-sm">{t('shell:context.loadError')}</span>;
  }

  if (eligible.length === 0) {
    return <span className="text-sm text-muted-foreground">{t('shell:context.none')}</span>;
  }

  return (
    <Select
      {...(active ? { value: active.organizationId } : {})}
      onValueChange={(organizationId) => {
        const organization = eligible.find((candidate) => candidate.id === organizationId);
        if (organization) {
          selectContext({ tenantId: organization.tenantId, organizationId: organization.id });
        }
      }}
    >
      <SelectTrigger size="sm" aria-label={t('shell:context.active')}>
        <SelectValue placeholder={t('shell:context.placeholder')} />
      </SelectTrigger>
      <SelectContent>
        {eligible.map((organization) => (
          <SelectItem key={organization.id} value={organization.id}>
            {organization.name ?? organization.code}
          </SelectItem>
        ))}
      </SelectContent>
    </Select>
  );
}
